// TelcoTrack — VBř P0: právní pokrytí po nemovitosti, ne po kruhu spoluvlastníků.
// CharOs 3/4/5 je jen nápověda, nikoli důkaz způsobilosti k §104a.

import type { BremenaSuggestedLegalPath } from '../constants/bremena.constants';
import { BREMENA_CHAROS_KOD_104A_ELIGIBLE } from '../constants/bremena.constants';

export const BREMENA_SECTION_104A_ELIGIBILITY = [
  'ELIGIBLE',
  'NOT_ELIGIBLE',
  'NEEDS_REVIEW',
] as const;
export type BremenaSection104aEligibility = (typeof BREMENA_SECTION_104A_ELIGIBILITY)[number];

export const BREMENA_DECISION_VALIDITY = ['VALID', 'REVIEW_REQUIRED', 'SUPERSEDED'] as const;
export type BremenaDecisionValidity = (typeof BREMENA_DECISION_VALIDITY)[number];

export const BREMENA_IMPACT_TRIGGERS = [
  'owners_shares_sjm',
  'route_device_access_land',
  'parcels_split_renumber',
  'entitled_investor_representation',
  'gp_extent',
  'permit_version',
  'route_removed',
] as const;
export type BremenaImpactTrigger = (typeof BREMENA_IMPACT_TRIGGERS)[number];

export interface BremenaParcelOwnerCoverage {
  subjectId: string;
  charOsKod?: string | null;
  shareNumerator?: number | null;
  shareDenominator?: number | null;
}

export interface BremenaParcelCoverageInput {
  parcelId: string;
  affectId?: string | null;
  owners: readonly BremenaParcelOwnerCoverage[];
}

export interface BremenaParcelLegalEvaluation {
  parcelId: string;
  affectId?: string | null;
  /** CharOs-based hint only — never legal proof. */
  section104aEligibility: BremenaSection104aEligibility;
  eligibilityReasons: string[];
  mixedPublicPrivate: boolean;
  suggestedLegalPath: BremenaSuggestedLegalPath | null;
  compatibleForSharedContract: boolean;
}

export interface BremenaCircleLegalProposal {
  /** Organizational circle may list many parcels; this is NOT a legal unit. */
  suggestedLegalPath: BremenaSuggestedLegalPath | null;
  recommendationCode: string;
  section104aEligibility: BremenaSection104aEligibility;
  mixedPublicPrivate: boolean;
  /** True when any parcel mix or parcel paths disagree — never §104a for the whole circle. */
  blockCircle104a: boolean;
  compatibleForSharedContract: boolean;
  reasons: string[];
  parcelEvaluations: BremenaParcelLegalEvaluation[];
}

export interface BremenaDecisionImpactResult {
  decisionValidity: BremenaDecisionValidity;
  /** Keep confirmed_legal_path in history; this only changes usability. */
  keepConfirmedLegalPath: true;
  messages: string[];
  triggers: BremenaImpactTrigger[];
}

const PUBLIC_HINT = new Set<string>(BREMENA_CHAROS_KOD_104A_ELIGIBLE);

function normalizeCharOs(charOsKod: string | null | undefined): string {
  return (charOsKod ?? '').trim();
}

export function isBremenaPublicCharOsHint(charOsKod: string | null | undefined): boolean {
  return PUBLIC_HINT.has(normalizeCharOs(charOsKod));
}

/**
 * CharOs 3/4/5 / 20 → nápověda ELIGIBLE | NOT_ELIGIBLE | NEEDS_REVIEW.
 * Nikdy to není právní důkaz §104a (chybí režim pozemku, souhlas, rozsah GP, …).
 */
export function evaluateCharOs104aHint(
  charOsKod: string | null | undefined,
): { eligibility: BremenaSection104aEligibility; reasons: string[] } {
  const kod = normalizeCharOs(charOsKod);
  if (!kod) {
    return {
      eligibility: 'NEEDS_REVIEW',
      reasons: ['Chybí CharOs — §104a nelze posoudit jen z kódu osoby.'],
    };
  }
  if (kod === '20') {
    return {
      eligibility: 'NOT_ELIGIBLE',
      reasons: ['CharOs 20 (PO / povinný subjekt) nikdy nenapovídá §104a.'],
    };
  }
  if (PUBLIC_HINT.has(kod)) {
    return {
      eligibility: 'ELIGIBLE',
      reasons: [
        `CharOs ${kod} napovídá veřejného vlastníka (stát/obec/kraj) — jen hint, ne důkaz §104a.`,
      ],
    };
  }
  return {
    eligibility: 'NOT_ELIGIBLE',
    reasons: [`CharOs ${kod} napovídá soukromého vlastníka — §104a se nenavrhuje.`],
  };
}

export function evaluateParcelLegalCoverage(
  parcel: BremenaParcelCoverageInput,
): BremenaParcelLegalEvaluation {
  const reasons: string[] = [];
  const owners = parcel.owners ?? [];
  if (!owners.length) {
    return {
      parcelId: parcel.parcelId,
      affectId: parcel.affectId,
      section104aEligibility: 'NEEDS_REVIEW',
      eligibilityReasons: ['Nemovitost nemá vlastníky — právní větev nelze navrhnout.'],
      mixedPublicPrivate: false,
      suggestedLegalPath: null,
      compatibleForSharedContract: false,
    };
  }

  const hints = owners.map((o) => evaluateCharOs104aHint(o.charOsKod));
  const hasPublic = hints.some((h) => h.eligibility === 'ELIGIBLE');
  const hasPrivate = hints.some((h) => h.eligibility === 'NOT_ELIGIBLE');
  const hasUnknown = hints.some((h) => h.eligibility === 'NEEDS_REVIEW');
  const mixedPublicPrivate = hasPublic && hasPrivate;

  for (const h of hints) reasons.push(...h.reasons);

  if (mixedPublicPrivate) {
    reasons.push(
      'Smíšené spoluvlastnictví veřejný+soukromý na jedné nemovitosti — expertní posouzení, nikoli §104a.',
    );
    return {
      parcelId: parcel.parcelId,
      affectId: parcel.affectId,
      section104aEligibility: 'NEEDS_REVIEW',
      eligibilityReasons: reasons,
      mixedPublicPrivate: true,
      suggestedLegalPath: null,
      compatibleForSharedContract: false,
    };
  }

  if (hasUnknown) {
    reasons.push('Nejasný CharOs u některého vlastníka — právní větev k posouzení.');
    return {
      parcelId: parcel.parcelId,
      affectId: parcel.affectId,
      section104aEligibility: 'NEEDS_REVIEW',
      eligibilityReasons: reasons,
      mixedPublicPrivate: false,
      suggestedLegalPath: null,
      compatibleForSharedContract: false,
    };
  }

  if (hasPublic && !hasPrivate) {
    return {
      parcelId: parcel.parcelId,
      affectId: parcel.affectId,
      section104aEligibility: 'ELIGIBLE',
      eligibilityReasons: reasons,
      mixedPublicPrivate: false,
      suggestedLegalPath: 'zakonna_104a',
      compatibleForSharedContract: true,
    };
  }

  return {
    parcelId: parcel.parcelId,
    affectId: parcel.affectId,
    section104aEligibility: 'NOT_ELIGIBLE',
    eligibilityReasons: reasons,
    mixedPublicPrivate: false,
    suggestedLegalPath: 'smluvni_sluzebnost',
    compatibleForSharedContract: true,
  };
}

/**
 * Kruh spoluvlastníků je jen organizační pohled.
 * Právní větev a pokrytí se skládají z nemovitostí; smíšený kruh NIKDY nedostane §104a.
 */
export function proposeLegalPathForOwnerCircle(
  parcels: readonly BremenaParcelCoverageInput[],
): BremenaCircleLegalProposal {
  const parcelEvaluations = parcels.map((p) => evaluateParcelLegalCoverage(p));
  const reasons = parcelEvaluations.flatMap((p) => p.eligibilityReasons);
  const mixedPublicPrivate = parcelEvaluations.some((p) => p.mixedPublicPrivate);
  const paths = [
    ...new Set(parcelEvaluations.map((p) => p.suggestedLegalPath).filter((p): p is BremenaSuggestedLegalPath => !!p)),
  ];
  const anyNullPath = parcelEvaluations.some((p) => p.suggestedLegalPath == null);
  const pathsDisagree = paths.length > 1 || (paths.length === 1 && anyNullPath && parcelEvaluations.length > 1);
  const blockCircle104a =
    mixedPublicPrivate ||
    pathsDisagree ||
    (paths.includes('zakonna_104a') && paths.includes('smluvni_sluzebnost')) ||
    parcelEvaluations.some((p) => p.section104aEligibility === 'NEEDS_REVIEW');

  if (!parcelEvaluations.length) {
    return {
      suggestedLegalPath: null,
      recommendationCode: 'NEURCENO',
      section104aEligibility: 'NEEDS_REVIEW',
      mixedPublicPrivate: false,
      blockCircle104a: true,
      compatibleForSharedContract: false,
      reasons: ['Kruh nemá nemovitosti.'],
      parcelEvaluations,
    };
  }

  if (mixedPublicPrivate || pathsDisagree || anyNullPath) {
    if (mixedPublicPrivate) {
      reasons.push(
        'Smíšené veřejné+soukromé spoluvlastnictví — nenavrhovat §104a pro celý kruh; expertní posouzení.',
      );
    }
    if (pathsDisagree) {
      reasons.push(
        'Propojené nemovitosti mají různé vlastnické režimy — neslučovat do jedné smlouvy, dokud nejsou podmínky slučitelné.',
      );
    }
    return {
      suggestedLegalPath: null,
      recommendationCode: 'K_POSOUZENI_EXPERT',
      section104aEligibility: 'NEEDS_REVIEW',
      mixedPublicPrivate,
      blockCircle104a: true,
      compatibleForSharedContract: false,
      reasons,
      parcelEvaluations,
    };
  }

  const only104a = paths.length === 1 && paths[0] === 'zakonna_104a';
  const onlySosb = paths.length === 1 && paths[0] === 'smluvni_sluzebnost';

  if (only104a) {
    return {
      suggestedLegalPath: 'zakonna_104a',
      recommendationCode: 'DOPORUCENO_104A',
      section104aEligibility: 'ELIGIBLE',
      mixedPublicPrivate: false,
      blockCircle104a: false,
      compatibleForSharedContract: true,
      reasons,
      parcelEvaluations,
    };
  }

  if (onlySosb) {
    return {
      suggestedLegalPath: 'smluvni_sluzebnost',
      recommendationCode: 'DOPORUCENO_SOSB',
      section104aEligibility: 'NOT_ELIGIBLE',
      mixedPublicPrivate: false,
      blockCircle104a: true,
      compatibleForSharedContract: true,
      reasons,
      parcelEvaluations,
    };
  }

  return {
    suggestedLegalPath: null,
    recommendationCode: 'NEURCENO',
    section104aEligibility: 'NEEDS_REVIEW',
    mixedPublicPrivate: false,
    blockCircle104a: true,
    compatibleForSharedContract: false,
    reasons,
    parcelEvaluations,
  };
}

const IMPACT_MESSAGES_CS: Record<BremenaImpactTrigger, string> = {
  owners_shares_sjm:
    'Nový vlastník / změna podílů nebo SJM na parcele — potvrzená větev zůstává v historii, ale použitelnost vyžaduje kontrolu právní a smluvní kontinuity (konečná smlouva).',
  route_device_access_land:
    'Změna trasy / zařízení / přístupového pozemku — znovu posoudit rozsah břemene a pokrytí smluv.',
  parcels_split_renumber:
    'Dělení nebo přečíslování parcel — vazby na podepsané dokumenty se nemaží; zkontrolujte identitu nemovitosti.',
  entitled_investor_representation:
    'Změna oprávněného / investora / zastoupení — ověřte, kdo smí podepsat.',
  gp_extent: 'Změna skutečného rozsahu GP — zkontrolujte soulad se smlouvou.',
  permit_version: 'Změna verze povolení — zkontrolujte návaznost titulu.',
  route_removed:
    'Parcela vypadla z trasy — pokud existuje smlouva, platba nebo podání, založte úkol na ukončení / vypořádání.',
};

/**
 * Reimport nesmí mazat podepsané dokumenty ani stav podpisu.
 * Mění jen výsledek kontroly použitelnosti (decision_validity).
 */
export function applyBremenaDecisionImpact(
  confirmedLegalPath: string | null | undefined,
  triggers: readonly BremenaImpactTrigger[],
): BremenaDecisionImpactResult {
  const unique = [...new Set(triggers)];
  if (!confirmedLegalPath || !unique.length) {
    return {
      decisionValidity: 'VALID',
      keepConfirmedLegalPath: true,
      messages: [],
      triggers: unique,
    };
  }
  const messages = unique.map((t) => IMPACT_MESSAGES_CS[t]);
  const validity: BremenaDecisionValidity = unique.includes('route_removed')
    ? 'SUPERSEDED'
    : 'REVIEW_REQUIRED';
  return {
    decisionValidity: validity,
    keepConfirmedLegalPath: true,
    messages,
    triggers: unique,
  };
}

export function bremenaNewOwnerContinuityMessage(parcelLabel?: string | null): string {
  const where = (parcelLabel ?? '').trim();
  const place = where ? ` na parcele ${where}` : ' na parcele';
  return (
    `Nový vlastník${place} — SoSB zůstává v historii, ale konečná smlouva potřebuje kontrolu právní/smluvní kontinuity.`
  );
}

export function canConfirmBremenaCirclePath(
  proposal: BremenaCircleLegalProposal,
  requestedPath: string | null | undefined,
): { ok: boolean; reason: string | null } {
  if (proposal.blockCircle104a && (requestedPath ?? proposal.suggestedLegalPath) === 'zakonna_104a') {
    return {
      ok: false,
      reason:
        'Nelze potvrdit §104a pro celý kruh (smíšené nebo neslučitelné nemovitosti). Posuzujte po nemovitostech.',
    };
  }
  if (!proposal.compatibleForSharedContract) {
    return {
      ok: false,
      reason:
        'Podmínky nemovitostí v kruhu nejsou slučitelné — společnou smlouvu nelze potvrdit, dokud je nerozdělíte nebo neposoudí expert.',
    };
  }
  return { ok: true, reason: null };
}
